import { lazy } from "react"
import { createBrowserRouter, redirect, Outlet } from "react-router-dom"
import { Layout } from "./layout"
import { RouteErrorBoundary } from "./routeError"
import { Providers } from "./providers"
import { AuthGate } from "./authGate"
import { AdminGate } from "./adminGate"

// Public pages — kept eager (critical path / small)
import { SignInPage } from "@/pages/SignIn"
import { NotFoundPage } from "@/pages/NotFound"
import { NotAuthorizedPage } from "@/pages/NotAuthorized"
import AuthProbe from "@/pages/AuthProbe"

// Authed pages — lazy-loaded so they don't bloat the initial bundle.
// Each becomes its own JS chunk fetched on first route match; a Suspense
// boundary in Layout renders the Outlet.
// Recover from stale/rotating chunk hashes around a Cloudflare Pages redeploy.
// During the brief propagation window a freshly-rotated chunk can 404/500, and
// an open tab still references the old filename. We:
//   1. Retry the import a few times with backoff — rides out the deploy window
//      without disrupting the user (no reload needed in the common case).
//   2. If still failing, force ONE reload to pull a fresh HTML + chunk manifest.
// The reload is timestamp-guarded (not single-shot): it can self-heal across a
// longer propagation window, but a genuinely broken deploy stops reloading
// after the guard interval and surfaces the error boundary instead of looping.
const isChunkLoadError = (err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err)
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)
}
const lz = (loader: () => Promise<Record<string, unknown>>, key: string) =>
  lazy(async () => {
    let lastErr: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const mod = await loader()
        sessionStorage.removeItem("chunk-reload-at")
        return { default: mod[key] as React.ComponentType }
      } catch (err) {
        lastErr = err
        if (!isChunkLoadError(err)) throw err
        if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
      }
    }
    const last = Number(sessionStorage.getItem("chunk-reload-at") || 0)
    const now = Date.now()
    if (now - last > 20_000) {
      sessionStorage.setItem("chunk-reload-at", String(now))
      window.location.reload()
      return { default: (() => null) as React.ComponentType }
    }
    throw lastErr
  })

// Entity list pages
const UnicornPredictionListPage = lz(() => import("@/pages/UnicornPredictionList"), "UnicornPredictionListPage")
const UnicornPredictionLayout = lz(() => import("@/pages/UnicornPredictionLayout"), "UnicornPredictionLayout")
const UnicornPredictionAnalyticsPage = lz(() => import("@/pages/UnicornPredictionAnalytics"), "UnicornPredictionAnalyticsPage")
const UnicornPredictionExecutiveReportPage = lz(() => import("@/pages/UnicornPredictionExecutiveReport"), "UnicornPredictionExecutiveReportPage")
const UnicornCapturePerformancePage = lz(() => import("@/pages/UnicornCapturePerformance"), "UnicornCapturePerformancePage")
const TaggingPage = lz(() => import("@/pages/TaggingPage"), "TaggingPage")
const SpecterAdminSearchPage = lz(() => import("@/pages/SpecterAdminSearch"), "SpecterAdminSearchPage")
const CompaniesListPage = lz(() => import("@/pages/CompaniesList"), "CompaniesListPage")
const CompaniesInsightsPage = lz(() => import("@/pages/CompaniesInsights"), "CompaniesInsightsPage")
const CompanyDashboardPage = lz(() => import("@/pages/CompanyDashboard"), "CompanyDashboardPage")
const PeopleListPage = lz(() => import("@/pages/PeopleList"), "PeopleListPage")
const PersonDashboardPage = lz(() => import("@/pages/PersonDashboard"), "PersonDashboardPage")
const InvestorsListPage = lz(() => import("@/pages/InvestorsList"), "InvestorsListPage")
const InvestorDashboardPage = lz(() => import("@/pages/InvestorDashboard"), "InvestorDashboardPage")
const AgentRunsListPage = lz(() => import("@/pages/AgentRunsList"), "AgentRunsListPage")

// Admin pages
const AdminMasterListsCompaniesPage = lz(() => import("@/pages/AdminMasterListsCompanies"), "AdminMasterListsCompaniesPage")
const AdminMasterListsPeoplePage = lz(() => import("@/pages/AdminMasterListsPeople"), "AdminMasterListsPeoplePage")
const AdminMasterListsInvestorsPage = lz(() => import("@/pages/AdminMasterListsInvestors"), "AdminMasterListsInvestorsPage")
const AdminAuditActionsPage = lz(() => import("@/pages/AdminAuditActions"), "AdminAuditActionsPage")
const AdminAuditSessionsPage = lz(() => import("@/pages/AdminAuditSessions"), "AdminAuditSessionsPage")
const AdminPermissionsPage = lz(() => import("@/pages/AdminPermissions"), "AdminPermissionsPage")
const AdminRolesPage = lz(() => import("@/pages/AdminRoles"), "AdminRolesPage")
const AdminUsersPage = lz(() => import("@/pages/AdminUsers"), "AdminUsersPage")

// Operator pages
const ExportsPage = lz(() => import("@/pages/Exports"), "ExportsPage")
const ImportJobsPage = lz(() => import("@/pages/ImportJobs"), "ImportJobsPage")
const IngestionJobsPage = lz(() => import("@/pages/IngestionJobs"), "IngestionJobsPage")

// Settings
const SettingsPage = lz(() => import("@/pages/Settings"), "SettingsPage")

// Runtime surface gating
import { isSurfaceEnabled } from "@/lib/ui-control-plane/runtimeAvailability"
import type { SurfaceId } from "@/lib/ui-control-plane"
import { AGENT_CONSOLE_GROUPS } from "@/lib/agents-nav"

/** Loader guard: redirects to /not-found if the surface is disabled. */
function surfaceGuard(id: SurfaceId) {
  return () => (isSurfaceEnabled(id) ? null : redirect("/not-found"))
}

export const router = createBrowserRouter([
  { path: "/__probe", element: (<Providers><AuthProbe /></Providers>) },

  // Public sign-in page
  {
    path: "/sign-in",
    element: (<Providers><SignInPage /></Providers>),
  },

  // Authed app shell
  {
    path: "/",
    element: (
      <Providers>
        <AuthGate>
          <Layout />
        </AuthGate>
      </Providers>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // Root redirect
      {
        index: true,
        loader: () => redirect("/companies"),
      },

      // ---------------------------------------------------------------
      // Main entity surfaces (order: Companies, People, Investors)
      // ---------------------------------------------------------------

      // Companies — direct path, no /list sub-route needed
      {
        path: "companies",
        children: [
          {
            index: true,
            loader: surfaceGuard("companies_list"),
            element: <CompaniesListPage />,
          },
          // Legacy /companies/list still works
          {
            path: "list",
            loader: () => redirect("/companies"),
          },
          {
            path: "insights",
            loader: surfaceGuard("companies_insights"),
            element: <CompaniesInsightsPage />,
          },
          {
            path: ":companyId",
            element: <CompanyDashboardPage />,
          },
        ],
      },

      // People
      {
        path: "people",
        children: [
          {
            index: true,
            element: <PeopleListPage />,
          },
          { path: "list", loader: () => redirect("/people") },
          {
            path: ":personId",
            element: <PersonDashboardPage />,
          },
        ],
      },

      // Investors
      {
        path: "investors",
        children: [
          {
            index: true,
            element: <InvestorsListPage />,
          },
          { path: "list", loader: () => redirect("/investors") },
          {
            path: ":investorId",
            element: <InvestorDashboardPage />,
          },
        ],
      },

      // Agentic Console. Path-based surfaces mirror the Admin console's
      // /admin/<section>/<subtab>: /agents/<group>/<surface>. Overview and
      // Personas are single-segment specials; bare /agents → Overview, and a
      // bare group (/agents/definitions) → that group's first surface.
      {
        path: "agents",
        children: [
          { index: true, loader: () => redirect("/agents/overview") },
          { path: "overview", element: <AgentRunsListPage /> },
          { path: "personas", element: <AgentRunsListPage /> },
          { path: ":group/:view", element: <AgentRunsListPage /> },
          {
            path: ":group",
            loader: ({ params }) => {
              const g = AGENT_CONSOLE_GROUPS.find((x) => x.slug === params.group)
              return redirect(g ? `/agents/${g.slug}/${g.views[0]}` : "/agents/overview")
            },
          },
        ],
      },

      // Unicorn Prediction (UP) — derived ranking surface
      // Layout owns the page header + Tabular / Analytics sub-tabs; child
      // routes render inside <Outlet/>.
      {
        path: "up",
        loader: surfaceGuard("up_list"),
        element: <UnicornPredictionLayout />,
        children: [
          { index: true, element: <UnicornPredictionListPage /> },
          { path: "analytics", element: <UnicornPredictionAnalyticsPage /> },
          { path: "performance", element: <UnicornCapturePerformancePage /> },
          { path: "executive", element: <UnicornPredictionExecutiveReportPage /> },
        ],
      },

      // Tagging surface — Company / Investor / Person tags via the
      // unified EntityTag store. Sidebar entry next to People.
      {
        path: "tags",
        element: <TaggingPage />,
      },

      {
        path: "admin/specter-search",
        element: <SpecterAdminSearchPage />,
      },

      // ---------------------------------------------------------------
      // Admin Console (admin-gated)
      // ---------------------------------------------------------------
      {
        path: "admin",
        element: (<AdminGate><Outlet /></AdminGate>),
        children: [
          {
            index: true,
            loader: () => redirect("/admin/master-lists/companies"),
          },

          // Master Lists
          {
            path: "master-lists",
            children: [
              {
                index: true,
                loader: () => redirect("/admin/master-lists/companies"),
              },
              {
                path: "companies",
                loader: surfaceGuard("admin_master_companies"),
                element: <AdminMasterListsCompaniesPage />,
              },
              {
                path: "people",
                loader: surfaceGuard("admin_master_people"),
                element: <AdminMasterListsPeoplePage />,
              },
              {
                path: "investors",
                loader: surfaceGuard("admin_master_investors"),
                element: <AdminMasterListsInvestorsPage />,
              },
            ],
          },

          // Jobs & Operations
          {
            path: "jobs",
            children: [
              {
                index: true,
                loader: () => redirect("/admin/jobs/imports"),
              },
              { path: "imports", element: <ImportJobsPage /> },
              { path: "exports", element: <ExportsPage /> },
              { path: "ingestion", element: <IngestionJobsPage /> },
            ],
          },

          // Audit
          {
            path: "audit",
            children: [
              {
                index: true,
                loader: () => redirect("/admin/audit/actions"),
              },
              { path: "actions", element: <AdminAuditActionsPage /> },
              { path: "sessions", element: <AdminAuditSessionsPage /> },
            ],
          },

          // User Management
          {
            path: "user-management",
            children: [
              {
                index: true,
                loader: () => redirect("/admin/user-management/users"),
              },
              { path: "permissions", element: <AdminPermissionsPage /> },
              { path: "roles", element: <AdminRolesPage /> },
              { path: "users", element: <AdminUsersPage /> },
            ],
          },

          // Legacy users route
          {
            path: "users",
            loader: () => redirect("/admin/user-management/users"),
          },
        ],
      },

      // ---------------------------------------------------------------
      // Legacy operator routes — redirect to admin
      // ---------------------------------------------------------------
      { path: "exports", loader: () => redirect("/admin/jobs/exports") },
      { path: "imports", loader: () => redirect("/admin/jobs/imports") },
      { path: "ingestion", loader: () => redirect("/admin/jobs/ingestion") },

      // Settings
      {
        path: "settings",
        loader: surfaceGuard("settings"),
        element: <SettingsPage />,
      },

      // Not authorized
      {
        path: "not-authorized",
        element: <NotAuthorizedPage />,
      },
    ],
  },

  // Not found
  {
    path: "*",
    element: <NotFoundPage />,
  },
])
